import { lazy, Suspense } from "react";
import { Header } from "@/components/Header";
import { HeroSection } from "@/components/HeroSection";
import { WhyChooseUs } from "@/components/WhyChooseUs";
import { ServicesSection } from "@/components/ServicesSection";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { Helmet } from "react-helmet-async";

// Lazy-load below-the-fold sections to reduce initial JS bundle
const TechnologySection = lazy(() => import("@/components/TechnologySection").then(m => ({ default: m.TechnologySection })));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection").then(m => ({ default: m.TestimonialsSection })));
const FAQSection = lazy(() => import("@/components/FAQSection").then(m => ({ default: m.FAQSection })));
const BookingSection = lazy(() => import("@/components/BookingSection").then(m => ({ default: m.BookingSection })));
const LocationSection = lazy(() => import("@/components/LocationSection").then(m => ({ default: m.LocationSection })));
const CTASection = lazy(() => import("@/components/CTASection").then(m => ({ default: m.CTASection })));
const Footer = lazy(() => import("@/components/Footer").then(m => ({ default: m.Footer })));

const Index = () => {
  // Local Business structured data with GMB Place ID
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "@id": "https://smilecaredentalgandhinagar.lovable.app/#dentist",
    name: "Smile Care Dental | Dental Clinic in Gandhinagar | Implants & Braces",
    image: [
      "https://smilecaredentalgandhinagar.lovable.app/hero-dental.webp",
    ],
    logo: "https://smilecaredentalgandhinagar.lovable.app/favicon.png",
    url: "https://smilecaredentalgandhinagar.lovable.app",
    telephone: "+916353963147",
    priceRange: "₹₹",
    currenciesAccepted: "INR",
    paymentAccepted: "Cash, Credit Card, UPI",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Pramukh Arcade 2, A-212, near Terracotta, opposite NID college",
      addressLocality: "Kudasan",
      addressRegion: "Gujarat",
      postalCode: "382421",
      addressCountry: "IN",
    },
    areaServed: [
      { "@type": "City", name: "Gandhinagar" },
      { "@type": "Place", name: "Kudasan" },
    ],
    geo: {
      "@type": "GeoCoordinates",
      latitude: 23.1851497,
      longitude: 72.6307143,
    },
    hasMap: "https://g.co/kgs/f7ebfd62eba9ea17",
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:30",
        closes: "20:30",
      },
    ],
    sameAs: [
      "https://www.facebook.com/61577465504095/",
      "https://www.instagram.com/smilecaredental2025/",
      "https://g.co/kgs/f7ebfd62eba9ea17",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      bestRating: "5",
      worstRating: "1",
      reviewCount: "120",
    },
    availableService: [
      "Dental Implants",
      "Root Canal Treatment",
      "Orthodontic Treatment (Braces)",
      "Cosmetic Dentistry",
      "Teeth Whitening",
      "Veneers & Crowns",
      "Dentures & Bridges",
      "Pediatric Dentistry",
      "Oral Surgery",
      "Laser Dentistry",
      "Teeth Cleaning",
      "Fillings and Sealants",
      "Extractions",
      "Emergency Dental Care",
      "Dental X-ray",
      "Mouth Guards",
      "Bonding",
      "Teeth Reshaping",
    ].map((s) => ({
      "@type": "MedicalProcedure",
      name: s,
    })),
  };

  return (
    <>
      <Helmet>
        <title>Smile Care Dental | Dental Clinic in Gandhinagar</title>
        <meta
          name="description"
          content="Trusted multispeciality dental clinic in Kudasan, Gandhinagar. Painless root canal, implants & cosmetic dentistry by MDS specialists. Call 063539 63147."
        />
        <meta
          name="keywords"
          content="best dental clinic gandhinagar, dentist near me kudasan, root canal treatment gandhinagar, dental implants kudasan, cosmetic dentistry gandhinagar, painless dental treatment"
        />
        <link rel="canonical" href="https://smilecaredentalgandhinagar.lovable.app/" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Smile Care Dental | Best Dental Clinic in Gandhinagar" />
        <meta
          property="og:description"
          content="Gandhinagar's most trusted multispeciality dental clinic with painless treatments by MDS specialists."
        />
        <meta property="og:url" content="https://smilecaredentalgandhinagar.lovable.app/" />
        <meta property="og:image" content="https://smilecaredentalgandhinagar.lovable.app/hero-dental.webp" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Smile Care Dental | Best Dental Clinic in Gandhinagar" />
        <meta
          name="twitter:description"
          content="Gandhinagar's most trusted multispeciality dental clinic with painless treatments by MDS specialists."
        />
        <meta name="twitter:image" content="https://smilecaredentalgandhinagar.lovable.app/hero-dental.webp" />

        {/* Geo tags */}
        <meta name="geo.region" content="IN-GJ" />
        <meta name="geo.placename" content="Gandhinagar" />
        <meta name="geo.position" content="23.2;72.6" />
        <meta name="ICBM" content="23.2, 72.6" />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      <Header />
      <main>
        <HeroSection />
        <WhyChooseUs />
        <ServicesSection />
        <Suspense fallback={null}>
          <TechnologySection />
          <TestimonialsSection />
          <FAQSection />
          <BookingSection />
          <LocationSection />
          <CTASection />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
      <FloatingWhatsApp />
    </>
  );
};

export default Index;
